// entryVisibilityFields — 入室画面表示 2 フラグ (entryShowPreview /
// entryShowMembers) の「フィールド知識」共有モジュール
// (entry-preview-members-2026-07-21 案 A).
//
// なぜ共有するか: この 2 フラグは「作成時 (CreateRoomPage)」と「入室後
// (RoomSettingsModal)」の両方から設定できる可変設定で、ラベル・説明文・
// デフォルト解釈 (undefined = On) が画面ごとに二重管理されるとドリフトが
// 本物化する。フォームエンジン統合 (案 B) は過剰と判断し、**知識だけ**を
// この 1 ファイルに寄せる:
//   - label / note (文言)
//   - undefined = On の解釈 (`entryFlagInitial`)
//   - 作成 POST body の組み立て規約 (`entryVisibilitySettingsForCreate` —
//     On はキー省略 = settings JSON に書かない、Off のときだけ false)
//
// UI 側の共有チェックボックスは presentation/EntryVisibilityCheckbox.tsx
// (application 層は DOM を引かない、既存流儀)。

import type { RoomSettings } from '../infrastructure/dispatcher/DispatcherClient.js';

/** 外部契約 (PATCH / POST settings) と同じ field 名. */
export type EntryVisibilityFieldKey = 'entryShowPreview' | 'entryShowMembers';

export interface EntryVisibilityFieldSpec {
  readonly key: EntryVisibilityFieldKey;
  /** CB のラベル (作成フォーム・設定モーダル共通). */
  readonly label: string;
  /** CB 直下の説明文 (共通). */
  readonly note: string;
}

export const ENTRY_SHOW_PREVIEW_FIELD: EntryVisibilityFieldSpec = {
  key: 'entryShowPreview',
  label: '入室画面にキャンバスを表示する',
  note: '入室前の人に、少し前のキャンバスのプレビューが見えるようになります。',
};

export const ENTRY_SHOW_MEMBERS_FIELD: EntryVisibilityFieldSpec = {
  key: 'entryShowMembers',
  label: '入室画面に参加者を表示する',
  note: '入室前の人に、いま参加している人の表示名が見えるようになります。',
};

/** 表示順 (プレビュー → 参加者). 両画面ともこの順で並べる. */
export const ENTRY_VISIBILITY_FIELDS: readonly EntryVisibilityFieldSpec[] = [
  ENTRY_SHOW_PREVIEW_FIELD,
  ENTRY_SHOW_MEMBERS_FIELD,
];

/**
 * 入室画面表示フラグの初期値解釈. settings JSON 由来なので **undefined = On**
 * (= 既存全ルームにデフォルト On が効く server 側セマンティクスと一致).
 * CB 初期チェック状態は必ずこの関数を通して決める (`?? true` の直書き分散を
 * 防ぐ). 旧居: application/roomSettingsForm.ts (案 A の共有化でこちらへ移動).
 */
export const entryFlagInitial = (raw: boolean | undefined): boolean => raw !== false;

/**
 * 作成フォーム値 → `POST /rooms` の `settings` 断片.
 *
 * **On (デフォルト) はキーを省略する** — server 側は zod optional で、省略時は
 * settings JSON にキーが書かれず undefined = On のセマンティクスが維持される
 * (ffae8d2)。Off のときだけ明示 false を送る。
 */
export const entryVisibilitySettingsForCreate = (values: {
  readonly entryShowPreview: boolean;
  readonly entryShowMembers: boolean;
}): Partial<Pick<RoomSettings, 'entryShowPreview' | 'entryShowMembers'>> => ({
  ...(values.entryShowPreview ? {} : { entryShowPreview: false }),
  ...(values.entryShowMembers ? {} : { entryShowMembers: false }),
});
